import { Link } from 'react-router';
import { cn } from '@/lib/utils';

const BRAND_ASSET_ROOT = '/assets/brand';

/**
 * Isotipo — el hexágono facetado azul/púrpura con el rombo central.
 * Se usa siempre con fondo transparente (sin placa), sobre cualquier superficie.
 *
 * decorative: cuando acompaña a un wordmark de texto ("VIA"), márcalo como
 * decorativo para no duplicar el anuncio del nombre en lectores de pantalla.
 */
export function PrismaHexagon({ className, priority = false, decorative = false, ...rest }) {
  return (
    <img
      src={`${BRAND_ASSET_ROOT}/prisma_hexagon.svg`}
      alt={decorative ? '' : 'VIA'}
      aria-hidden={decorative ? true : undefined}
      className={cn('h-auto w-auto object-contain', className)}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      {...rest}
    />
  );
}

export function UcChristusLogo({ contrast = 'dark', className, priority = false }) {
  const source = contrast === 'light' ? 'logo_white_2x.png' : 'logo_dark_2x.png';

  return (
    <img
      src={`${BRAND_ASSET_ROOT}/${source}`}
      alt="UC CHRISTUS"
      className={cn('h-auto w-auto object-contain', className)}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
    />
  );
}

/**
 * Wordmark "VIA" en tipografía Inter.
 *
 * Tonos verificados contra WCAG 2.2 AA (texto normal, ratio >= 4.5:1):
 *  - contrast="dark"  (fondo claro #F8FAFC): VIA #671E75 = 9.9
 *  - contrast="light" (fondo púrpura #671E75 / oscuro azulado #11151C): VIA #FFFFFF
 */
export function PrismaViaWordmark({ contrast = 'dark', className }) {
  const light = contrast === 'light';
  return (
    <span className={cn('font-display font-extrabold leading-none tracking-tight', className)}>
      <span className={light ? 'text-white' : 'text-primary'}>VIA</span>
    </span>
  );
}

/**
 * Lockup de marca del producto: logo UC CHRISTUS + wordmark "VIA".
 *
 * variant:
 *  - "product" (default): logo + "VIA" + bajada "Valor de la IA"
 *  - "institutional": línea "UC CHRISTUS" arriba + "VIA"
 */
export function ViaBrandLockup({ contrast = 'dark', compact = false, variant = 'product', className, asLink = false }) {
  const light = contrast === 'light';

  const content = (
    <div className={cn('flex min-w-0 items-center', compact ? 'gap-2.5' : 'gap-3.5', className)}>
      <UcChristusLogo contrast={contrast} priority className={cn('shrink-0', compact ? 'h-5' : 'h-7')} />
      <span
        className={cn('h-7 w-px shrink-0', light ? 'bg-white/25' : 'bg-border', compact && 'h-5')}
        aria-hidden="true"
      />
      <span className="min-w-0">
        {variant === 'institutional' && (
          <span
            className={cn(
              'block text-[10px] font-semibold uppercase leading-none tracking-[0.08em]',
              compact && 'text-[9px]',
            )}>
            <span className={light ? 'text-[#A9C7F2]' : 'text-secondary'}>UC</span>{' '}
            <span className={light ? 'text-white/90' : 'text-[#3D1046]'}>CHRISTUS</span>
          </span>
        )}
        <span
          className={cn(
            'block font-display font-bold leading-none tracking-tight',
            compact ? 'text-sm' : 'text-base',
            variant === 'institutional' && 'mt-1',
            light ? 'text-white' : 'text-foreground',
          )}>
          VIA
        </span>
        {variant === 'product' && !compact && (
          <span
            className={cn(
              'mt-1 block truncate text-[9px] font-semibold uppercase tracking-[0.16em]',
              light ? 'text-white/60' : 'text-muted-foreground',
            )}>
            Valor de la IA
          </span>
        )}
      </span>
    </div>
  );

  if (!asLink) return content;

  return (
    <Link
      to="/"
      aria-label="VIA — Inicio"
      className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {content}
    </Link>
  );
}

export function InstitutionalBanner({ className }) {
  return (
    <section
      className={cn('py-6', className)}
      aria-label="Identidad UC CHRISTUS"
      style={{
        background:
          'linear-gradient(150deg, rgba(50, 15, 58, 0.95) 0%, rgba(103, 30, 117, 0.92) 52%, rgba(40, 90, 170, 0.88) 100%)',
      }}>
      <div className="mx-auto flex max-w-7xl items-center gap-5 px-6">
        <img
          src={`${BRAND_ASSET_ROOT}/logo_white_2x.png`}
          alt="UC CHRISTUS"
          className="h-5 w-auto object-contain sm:h-6"
          loading="lazy"
        />
        <span className="h-7 w-px bg-white/20" aria-hidden="true" />
        <div>
          <p className="text-xs font-medium text-white/90 sm:text-sm">La excelencia que tu salud merece</p>
          <p className="mt-0.5 text-[10px] text-white/55 sm:text-[11px]">Somos UC CHRISTUS, somos la Católica</p>
        </div>
      </div>
    </section>
  );
}

export function InstitutionalFooterSignature({ contrast = 'dark', className }) {
  return (
    <img
      src={`${BRAND_ASSET_ROOT}/${contrast === 'light' ? 'logo_white_2x.png' : 'footer_somos.png'}`}
      alt={contrast === 'light' ? 'UC CHRISTUS' : 'Somos UC CHRISTUS, somos la Católica'}
      className={cn('h-auto max-w-full object-contain', className)}
      loading="lazy"
    />
  );
}
